import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  OnDestroy,
  Input,
  inject,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ToastService } from '@olv/ui-shared/ui-shared/toast/toast.service';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { AppContextService } from '../../../../services/app-context.service';
import { Task, TaskFilterParams, TaskMapper } from '../../../../models/task.model';
import { Observable, Subject, shareReplay, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { PillSearchComponent, SearchState } from '@olv/ui-shared/ui-shared/pill-search/pill-search.component';
import { TaskResolutionEnum, TaskTypeEnum, TaskTypeFilterEnum } from '@olv/ui-shared/enums/task.enums';
import { MerchantService } from '@olv/ui-shared/services/merchant.service';
import { TaskService } from '@olv/ui-shared/services/task.service';
import { TaskMerchantDto, TaskUsersFilterResponseDto } from '@olv/ui-shared/models/task.model';
import { ShowMoreComponent } from '@olv/ui-shared/ui-shared/show-more/show-more.component';
import { enumToFilterFields } from '@olv/ui-shared/utils/enum-to-filter.util';
import { LoadingSpinnerComponent } from '@olv/ui-shared/ui-shared/loading-spinner/loading-spinner.component';
import { MatDialog } from '@angular/material/dialog';
import { FiltersDialogComponent } from '@olv/ui-shared/ui-shared/filters-dialog/filters.component';
import { AppliedFilter, FilterService } from '@olv/ui-shared/services/filter.service';
import { EmptyStateComponent } from '@olv/ui-shared/ui-shared/empty-state/empty-state.component';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';

@Component({
  selector: 'app-tasks-history',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
    PillSearchComponent,
    ShowMoreComponent,
    LoadingSpinnerComponent,
    EmptyStateComponent,
    ButtonComponent,
  ],
  templateUrl: './tasks-history.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./tasks-history.component.scss'],
})
export class TasksHistoryComponent implements OnInit, OnChanges, OnDestroy {
  private merchantService = inject(MerchantService);
  private taskService = inject(TaskService);
  private toastr = inject(ToastService);
  private router = inject(Router);
  private location = inject(Location);

  @Output() taskSelected = new EventEmitter<Task>();
  @Input() targetTaskHistoryId: string | null = null;
  @Input() searchText = '';

  searchValue = '';
  targetId: string | null = null;

  tasks: Task[] = [];
  selected: Task | null = null;
  filtersApplied = false;
  hasMore = true;
  isLoading = false;
  initialLoading = false;
  error: string | null = null;
  isFirstLoad = true;
  isDisableFilters = false;

  TaskTypeEnum = TaskTypeEnum;
  TaskResolutionEnum = TaskResolutionEnum;

  private readonly dialog = inject(MatDialog);
  private readonly filterService = inject(FilterService);
  private readonly appCtx = inject(AppContextService);
  isSettlementApp = this.appCtx.getAppName() === 'settlement';

  filterMap: Partial<TaskFilterParams> = {};
  allFilters: TaskFilterParams = {};
  filters = [
    { name: 'Task Type', filterFields: enumToFilterFields(this.getTaskTypeFilterEnum()) },
    {
      name: 'Status',
      filterFields: enumToFilterFields(TaskResolutionEnum),
    },
  ];

  preparedByOptions: { value: string; label: string }[] = [];
  resolvedByOptions: { value: string; label: string }[] = [];
  merchantOptions: { value: string; label: string }[] = [];

  private destroy$ = new Subject<void>();
  private currentPage = 0;
  private pageSize = 20;
  private taskUsers$!: Observable<TaskUsersFilterResponseDto>;
  private taskMerchants$!: Observable<TaskMerchantDto[]>;

  ngOnInit(): void {
    this.searchValue = this.searchText;
    this.targetId = this.targetTaskHistoryId;
    if (this.isSettlementApp) {
      this.loadTaskMerchants();
    }
    this.loadTaskUsers();
    this.loadHistory();
    this.location.go(this.router.url.split('?')[0]);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchText'] && !changes['searchText'].firstChange) {
      this.searchValue = this.searchText;
      this.resetAndLoadHistory();
    }

    if (changes['targetTaskHistoryId'] && !changes['targetTaskHistoryId'].firstChange) {
      this.targetId = this.targetTaskHistoryId;
      this.trySelectTargetTask();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadHistory(loadMore = false): void {
    if (loadMore) {
      this.isLoading = true;
    } else {
      this.initialLoading = true;
    }

    this.error = null;

    const filterParams = {
      maxResultCount: this.pageSize,
      skipCount: loadMore ? this.currentPage * this.pageSize : 0,
      searchText: this.searchValue || undefined,
      sorting: 'resolvedAt desc',
      ...this.filterMap,
    };
    this.allFilters = filterParams;

    this.taskService
      .getTaskHistory(filterParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (this.isFirstLoad) {
            this.isDisableFilters = response.items.length === 0 && !this.searchValue?.trim();
          }
          this.isFirstLoad = false;
          const newTasks = response.items.map((dto) => TaskMapper.toFrontendTask(dto));

          if (loadMore) {
            this.tasks = [...this.tasks, ...newTasks];
          } else {
            this.tasks = newTasks;
            this.currentPage = 0;
          }
          this.hasMore = response.items.length === this.pageSize;
          this.isLoading = false;
          this.initialLoading = false;
          this.trySelectTargetTask();
        },
        error: () => {
          this.error = 'Failed to load task history. Please try again.';
          this.isLoading = false;
          this.initialLoading = false;
          this.showError('Failed to load task history');
        },
      });
  }

  public resetAndLoadHistory(): void {
    this.currentPage = 0;
    this.loadHistory();
  }

  private showError(message: string): void {
    this.toastr.error(message);
  }

  matchTaskById(taskId: string): void {
    const targetTask = this.tasks.find((task) => task.id === taskId);
    if (targetTask) {
      this.onTaskClick(targetTask);
      this.targetId = null;
    }
  }

  private trySelectTargetTask(): void {
    if (this.targetId) {
      this.matchTaskById(this.targetId);
    }
  }
  getResolutionClass(resolution: TaskResolutionEnum): string {
    switch (resolution) {
      case TaskResolutionEnum.Approved:
        return 'resolution-approved';
      case TaskResolutionEnum.Rejected:
        return 'resolution-rejected';
      case TaskResolutionEnum.Cancelled:
        return 'resolution-cancelled';
      default:
        return '';
    }
  }

  toggleFilters(): void {
    this.selected = null;
    this.merchantService.closeSidebar();

    const dialogRef = this.dialog.open(FiltersDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        filters: this.filters,
        dropdownsOptions: this.getDropdownsOptions(),
        dateFiltersOptions: [
          { key: 'preparedDate', label: 'Prepared Date' },
          { key: 'resolvedDate', label: 'Resolved Date' },
        ],
        preselectedValues: this.getAppliedFiltersAsArray(),
        isDisableFilters: this.isDisabled,
      },
    });

    this.taskUsers$.pipe(takeUntil(dialogRef.afterClosed())).subscribe((response) => {
      dialogRef.componentInstance.updateDropdownOptions(
        'preparedBy',
        (response.preparedBy ?? []).map((user) => ({ value: user.userId, label: user.displayName })),
      );
      dialogRef.componentInstance.updateDropdownOptions(
        'resolvedBy',
        (response.resolvedBy ?? []).map((user) => ({ value: user.userId, label: user.displayName })),
      );
    });

    if (this.isSettlementApp) {
      this.taskMerchants$.pipe(takeUntil(dialogRef.afterClosed())).subscribe((items) => {
        dialogRef.componentInstance.updateDropdownOptions(
          'relatedMerchantId',
          (items ?? []).map((merchant) => ({ value: merchant.id, label: merchant.name })),
        );
      });
    }

    const sub = dialogRef.componentInstance.resetRequest.subscribe((result) => {
      this.onFiltersApplied(result);
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.onFiltersApplied(result);
      }

      sub.unsubscribe();
    });
  }

  clearFilters(): void {
    this.filterMap = {};
    this.filtersApplied = false;
    this.resetAndLoadHistory();
  }

  onFilterReset(): void {
    this.filterMap = {};
    this.filtersApplied = false;
    this.resetAndLoadHistory();
  }

  getAppliedFiltersAsArray() {
    return this.filterService.getAppliedFilters(this.filterMap);
  }

  onFiltersApplied(values: AppliedFilter[]) {
    this.filtersApplied = values.length > 0;

    this.filterMap = this.filterService.applySelectedFilters(values, {});

    this.loadHistory();
  }

  private loadTaskMerchants(): void {
    this.taskMerchants$ = this.taskService.getTaskMerchants().pipe(shareReplay(1));
    this.taskMerchants$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => {
        this.merchantOptions = (items ?? []).map((merchant) => ({ value: merchant.id, label: merchant.name }));
      },
    });
  }

  updateSearch(searchState: SearchState): void {
    if (this.searchValue === searchState.value) {
      return;
    }

    this.searchValue = searchState.value;
    this.resetAndLoadHistory();
  }

  loadMore(): void {
    if (!this.isLoading && this.hasMore) {
      this.currentPage++;
      this.loadHistory(true);
    }
  }

  onTaskClick(task: Task): void {
    this.selected = task;
    this.taskSelected.emit(task);
    this.merchantService.openSidebar();
  }

  getResolutionText(taskResolutionEnum: TaskResolutionEnum) {
    switch (taskResolutionEnum) {
      case TaskResolutionEnum.Approved:
        return 'Approved';
      case TaskResolutionEnum.Rejected:
        return 'Rejected';
      case TaskResolutionEnum.Cancelled:
        return 'Cancelled';
      default:
        return 'Pending';
    }
  }
  get isDisabled(): boolean {
    if (this.isFirstLoad) {
      return this.isLoading || this.initialLoading;
    }

    return this.isDisableFilters;
  }

  private loadTaskUsers(): void {
    this.taskUsers$ = this.taskService.getTaskUsers().pipe(shareReplay(1));
    this.taskUsers$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.preparedByOptions = (response.preparedBy ?? []).map((user) => ({
          value: user.userId,
          label: user.displayName,
        }));
        this.resolvedByOptions = (response.resolvedBy ?? []).map((user) => ({
          value: user.userId,
          label: user.displayName,
        }));
      },
    });
  }

  private getDropdownsOptions() {
    const dropdowns: any[] = [
      {
        key: 'preparedBy',
        label: 'Prepared By',
        placeholder: 'Select Prepared By',
        options: this.preparedByOptions,
        type: 'checkbox',
        isFilterClass: true,
        isSearch: true,
      },
      {
        key: 'resolvedBy',
        label: 'Resolved By',
        placeholder: 'Select Resolved By',
        options: this.resolvedByOptions,
        type: 'checkbox',
        isFilterClass: true,
        isSearch: true,
      },
    ];

    if (this.isSettlementApp) {
      dropdowns.push({
        key: 'relatedMerchantId',
        label: 'Merchants',
        placeholder: 'Select Merchant',
        options: this.merchantOptions,
        type: 'checkbox',
        isFilterClass: true,
        isSearch: true,
      });
    }

    return dropdowns;
  }

  private getTaskTypeFilterEnum() {
    if (this.isSettlementApp) {
      return {
        Whitelist: TaskTypeFilterEnum.Whitelist,
        Withdrawal: TaskTypeFilterEnum.Withdrawal,
        Deposit: TaskTypeFilterEnum.Deposit,
        'Merchant Change': TaskTypeFilterEnum.Merchant,
        Adjustment: TaskTypeFilterEnum.TransactionAdjustment,
        Reversal: TaskTypeFilterEnum.DepositReversal,
        'Unexpected Deposit': TaskTypeFilterEnum.UnexpectedDeposit,
        'Allowance Depleted': TaskTypeFilterEnum.DelegationAllowanceDepleted,
        'Reserve Depleted': TaskTypeFilterEnum.DelegationReserveDepleted,
        'Allowance Over-Spend': TaskTypeFilterEnum.DelegationAllowanceTopUpOverSpend,
        'Capacity Drift': TaskTypeFilterEnum.DelegationCapacityDrift,
        'Exchange Rate Required': TaskTypeFilterEnum.ExchangeRateRequired,
        'Pre-Launch EVM Deposit': TaskTypeFilterEnum.PreLaunchEvmDeposit,
        'Allowance Underfunded': TaskTypeFilterEnum.DelegationAllowanceUnderfunded,
      };
    }

    return {
      Whitelist: TaskTypeFilterEnum.Whitelist,
      Withdrawal: TaskTypeFilterEnum.Withdrawal,
    };
  }
}
